/**
 * Per-client Marketing Calendar.
 *
 * Renders the current month + next month stacked vertically. Each day
 * cell shows colour-coded activity pills (one per scheduled item) with
 * the platform's brand favicon. Clicking a day creates a new activity;
 * clicking an existing pill opens it for edit.
 *
 * Authoring is admin-only. Client-portal users see a read-only grid and
 * can approve / reject items that are sitting in `ready_for_signoff`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus,
  Maximize2, Minimize2,
} from "lucide-react";
import { PlatformIcon } from "@/components/PlatformIcon";
import { ActivityDialog } from "@/components/calendar/ActivityDialog";
import {
  STATUS_META, startOfMonth, addMonths, isoDate, buildMonthGrid, WEEKDAYS,
} from "@/components/calendar/calendarUtils";


export default function ClientCalendarPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Client contacts with `can_use_ai_studio` may use AI Studio + edit
  // calendar activities. Admins always have both.
  const canAiStudio = isAdmin || !!user?.can_use_ai_studio;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()));
  const monthA = anchor;
  const monthB = addMonths(anchor, 1);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | { mode:"new",day? } | { mode:"edit",activity }
  const [expandAll, setExpandAll] = useState(false);
  // Per-week expansion: set of "YYYY-MM-WW" keys (W is the Monday's iso date).
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set());

  // Range: 1st of month A → 1st of month after B
  const rangeStart = useMemo(() => isoDate(monthA), [monthA]);
  const rangeEnd = useMemo(() => isoDate(addMonths(anchor, 2)), [anchor]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/clients/${clientId}/calendar/activities`,
        { params: { start: rangeStart, end: rangeEnd } },
      );
      setActivities(data?.activities || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load calendar");
      setActivities([]);
    } finally { setLoading(false); }
  }, [clientId, rangeStart, rangeEnd]);

  useEffect(() => { reload(); }, [reload]);

  // Group activities by YYYY-MM-DD for O(1) lookup per cell.
  const byDay = useMemo(() => {
    const m = {};
    for (const a of activities) {
      const key = (a.scheduled_at || "").slice(0, 10);
      if (!key) continue;
      (m[key] = m[key] || []).push(a);
    }
    // Sort same-day activities by time.
    for (const k of Object.keys(m)) {
      m[k].sort((x, y) => (x.scheduled_at || "").localeCompare(y.scheduled_at || ""));
    }
    return m;
  }, [activities]);

  // Deep-link to a specific activity via ?activity=act_xxx
  const deepLinkId = searchParams.get("activity");
  useEffect(() => {
    if (!deepLinkId || loading) return;
    if (editing?.activity?.activity_id === deepLinkId) return;
    const match = activities.find((a) => a.activity_id === deepLinkId);
    if (match) setEditing({ mode: "edit", activity: match });
  }, [deepLinkId, activities, loading, editing]);

  const closeDialog = () => {
    setEditing(null);
    if (searchParams.has("activity")) {
      searchParams.delete("activity");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const toggleWeek = (weekKey) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekKey)) next.delete(weekKey);
      else next.add(weekKey);
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="client-calendar-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#002FA7]/10">
            <CalendarDays className="h-4 w-4 text-[#002FA7]" />
          </span>
          <div>
            <h2 className="font-display font-bold text-xl">Marketing Calendar</h2>
            <p className="text-[12px] text-zinc-500">
              Plan and schedule social posts, blog content, email campaigns and
              everything in between — two months at a glance.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setExpandAll((v) => !v)}
            data-testid="calendar-expand-all">
            {expandAll
              ? <><Minimize2 className="h-3.5 w-3.5 mr-1" /> Compact view</>
              : <><Maximize2 className="h-3.5 w-3.5 mr-1" /> Expand all</>}
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setAnchor(addMonths(anchor, -1))}
            data-testid="calendar-prev-month">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setAnchor(startOfMonth(new Date()))}
            data-testid="calendar-today">
            Today
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8"
            onClick={() => setAnchor(addMonths(anchor, 1))}
            data-testid="calendar-next-month">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          {isAdmin && (
            <Button size="sm"
              onClick={() => setEditing({ mode: "new" })}
              className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white"
              data-testid="calendar-new-activity">
              <Plus className="h-3.5 w-3.5 mr-1" /> New activity
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm">
          <CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading activities…
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <MonthGrid
            monthDate={monthA} byDay={byDay} isAdmin={isAdmin}
            expandAll={expandAll}
            expandedWeeks={expandedWeeks} onToggleWeek={toggleWeek}
            onCellClick={(d) => isAdmin && setEditing({ mode: "new", day: isoDate(d) })}
            onActivityClick={(a) => setEditing({ mode: "edit", activity: a })}
          />
          <MonthGrid
            monthDate={monthB} byDay={byDay} isAdmin={isAdmin}
            expandAll={expandAll}
            expandedWeeks={expandedWeeks} onToggleWeek={toggleWeek}
            onCellClick={(d) => isAdmin && setEditing({ mode: "new", day: isoDate(d) })}
            onActivityClick={(a) => setEditing({ mode: "edit", activity: a })}
          />
        </div>
      )}

      {editing && (
        <ActivityDialog
          clientId={clientId}
          mode={editing.mode}
          activity={editing.activity || null}
          defaultDay={editing.day || null}
          onClose={closeDialog}
          onSaved={() => { closeDialog(); reload(); }}
          onSilentReload={reload}
          isAdmin={isAdmin}
          canAiStudio={canAiStudio}
          navigate={navigate}
        />
      )}
    </div>
  );
}


export function MonthGrid({
  monthDate, byDay, isAdmin,
  expandAll = false, expandedWeeks = new Set(), onToggleWeek,
  onCellClick, onActivityClick,
}) {
  const days = useMemo(() => buildMonthGrid(monthDate), [monthDate]);
  const monthIndex = monthDate.getMonth();
  const monthLabel = monthDate.toLocaleDateString("en-AU", {
    month: "long", year: "numeric",
  });
  const todayKey = isoDate(new Date());

  // Split the 42 days into 6 weeks of 7.
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    weeks.push(days.slice(w * 7, w * 7 + 7));
  }

  return (
    <div data-testid={`calendar-month-${monthDate.getFullYear()}-${monthIndex + 1}`}>
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <h3 className="font-display font-bold text-lg tracking-tight">{monthLabel}</h3>
        <LegendStrip compact />
      </div>
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <div className="grid grid-cols-7 bg-zinc-50 border-b border-zinc-200">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-[10px] font-mono uppercase tracking-wider
              text-zinc-500 px-2 py-1.5 text-center">
              {w}
            </div>
          ))}
        </div>
        {weeks.map((week) => {
          const weekKey = isoDate(week[0]);
          const weekExpanded = expandAll || expandedWeeks.has(weekKey);
          // Skip rendering weeks that don't contain a single day of THIS
          // month — they're 100% overflow rows from the next month.
          const hasInMonth = week.some((d) => d.getMonth() === monthIndex);
          if (!hasInMonth) return null;
          return (
            <div key={weekKey} className="grid grid-cols-7"
              data-testid={`calendar-week-${weekKey}`}>
              {week.map((d) => {
                const inMonth = d.getMonth() === monthIndex;
                const key = isoDate(d);
                // Days from the previous/next month are blanked out so the
                // same date never appears in both stacked months.
                if (!inMonth) {
                  return (
                    <div key={key}
                      className="min-h-[96px] border-r border-b border-zinc-100 bg-zinc-100/60"
                      data-testid={`calendar-day-${key}-out`} />
                  );
                }
                const items = byDay[key] || [];
                const isToday = key === todayKey;
                const showAll = weekExpanded;
                const visible = showAll ? items : items.slice(0, 4);
                return (
                  <div
                    key={key}
                    className={`min-h-[96px] border-r border-b border-zinc-100 p-1.5 bg-white
                      ${isAdmin ? "cursor-pointer hover:bg-[#002FA7]/[0.03]" : ""}
                      transition-colors`}
                    onClick={() => onCellClick && onCellClick(d)}
                    data-testid={`calendar-day-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-mono
                        ${isToday
                          ? "bg-[#002FA7] text-white px-1.5 py-0.5 rounded-sm font-bold"
                          : "text-zinc-700"}`}>
                        {d.getDate()}
                      </span>
                      {items.length > 0 && (
                        <button type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleWeek && onToggleWeek(weekKey);
                          }}
                          title={weekExpanded ? "Collapse week" : "Expand this week to show all activities"}
                          className="text-[9px] font-mono text-zinc-500 hover:text-[#002FA7]
                            hover:bg-zinc-100 rounded-sm px-1 transition-colors"
                          data-testid={`calendar-day-count-${key}`}>
                          {items.length}
                        </button>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {visible.map((a) => (
                        <ActivityPill key={a.activity_id} a={a}
                          onClick={(e) => { e.stopPropagation(); onActivityClick && onActivityClick(a); }} />
                      ))}
                      {!showAll && items.length > 4 && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); onToggleWeek && onToggleWeek(weekKey); }}
                          className="text-[9px] text-[#002FA7] hover:underline px-1">
                          +{items.length - 4} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export function ActivityPill({ a, onClick }) {
  const meta = STATUS_META[a.status] || STATUS_META.planned;
  const time = (a.scheduled_at || "").slice(11, 16);
  // Build a richer tooltip — browsers show it on hover.
  const tooltip = [
    a.title,
    time && `at ${time}`,
    `Status: ${meta.label}`,
    a.client_name && `Client: ${a.client_name}`,
    a.business_unit_name && `Property: ${a.business_unit_name}`,
    a.description && `\n${a.description}`,
    a.post_content && `\n${a.post_content.slice(0, 140)}${a.post_content.length > 140 ? "…" : ""}`,
  ].filter(Boolean).join("  ·  ");
  return (
    <button type="button" onClick={onClick}
      title={tooltip}
      data-testid={`calendar-activity-${a.activity_id}`}
      className="w-full text-left rounded-sm px-1 py-0.5 flex items-center gap-1
        hover:ring-1 hover:ring-zinc-300 transition-all"
      style={{ background: meta.bg }}>
      <PlatformIcon platform={a.platform} size={12} />
      {time && <span className="text-[9px] font-mono text-zinc-600">{time}</span>}
      <span className="text-[10px] truncate flex-1" style={{ color: meta.color }}>
        {a.title}
      </span>
    </button>
  );
}


export function LegendStrip({ compact = false }) {
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap text-[10px]">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1"
            data-testid={`legend-${k}`}>
            <span className="h-2 w-2 rounded-sm inline-block"
              style={{ background: v.bg, border: `1px solid ${v.color}33` }} />
            <span style={{ color: v.color }}>{v.label}</span>
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="border-t border-zinc-100 pt-3 flex items-center gap-3 flex-wrap text-[10px] text-zinc-500">
      <span className="label-overline">Status:</span>
      {Object.entries(STATUS_META).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm inline-block"
            style={{ background: v.bg, border: `1px solid ${v.color}33` }} />
          <span style={{ color: v.color }}>{v.label}</span>
        </span>
      ))}
    </div>
  );
}
